"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from "@/lib/utils";
import { Checkbox, Icon, Typography } from "@bookips/sds";
import * as Accordion from "@radix-ui/react-accordion";
import { forwardRef, memo, useCallback, useEffect, useState } from "react";
import { useScrollLock } from "usehooks-ts";
import { stepCodeLookup } from "./ProductListItem";
/** step 필터 아이템 표시 텍스트 변환 */ const getFilterItemLabel = (filterKey, item)=>{
    if (filterKey === "steps") {
        const stepNumber = Number(item);
        return stepCodeLookup[stepNumber] || item;
    }
    return item;
};
/**
 * 모바일 필터 사이드시트 컴포넌트
 * - 744px 이하에서 사용
 * - 오른쪽에서 슬라이드 인
 * - 아코디언 형태로 필터 카테고리 표시
 * - 체크박스로 다중 선택
 */ export const MobileFilterSheet = (param)=>{
    let { className , isOpen , onClose , filterItems , selectedFilters , onFilterChange , initialExpandedKey  } = param;
    const [expandedItems, setExpandedItems] = useState([]);
    const [wasOpen, setWasOpen] = useState(false);
    // 열릴 때만 initialExpandedKey가 있으면 해당 필터 열기, 없으면 첫 번째 필터 열기
    useEffect(()=>{
        // isOpen이 false -> true로 바뀔 때만 실행
        if (isOpen && !wasOpen) {
            if (initialExpandedKey) {
                setExpandedItems([
                    initialExpandedKey
                ]);
            } else if (filterItems.length > 0) {
                setExpandedItems([
                    filterItems[0].key
                ]);
            }
        }
        setWasOpen(isOpen);
    }, [
        isOpen,
        initialExpandedKey,
        filterItems,
        wasOpen
    ]);
    const { lock , unlock  } = useScrollLock({
        autoLock: false
    });
    // 열릴 때 body 스크롤 막기
    useEffect(()=>{
        if (isOpen) {
            lock();
        } else {
            unlock();
        }
        return ()=>unlock();
    }, [
        isOpen,
        lock,
        unlock
    ]);
    // ESC 키로 닫기
    useEffect(()=>{
        const handleKeyDown = (e)=>{
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return ()=>document.removeEventListener("keydown", handleKeyDown);
    }, [
        isOpen,
        onClose
    ]);
    // 체크박스 변경 핸들러
    const handleCheckboxChange = useCallback((filterKey, value, checked)=>{
        const currentValues = selectedFilters[filterKey] || [];
        if (checked) {
            onFilterChange(filterKey, [
                ...currentValues,
                value
            ]);
        } else {
            onFilterChange(filterKey, currentValues.filter((v)=>v !== value));
        }
    }, [
        selectedFilters,
        onFilterChange
    ]);
    if (!isOpen && !filterItems.length) return null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cn("fixed inset-0 z-[1400] bg-gray-900/50 transition-opacity duration-300", isOpen ? "opacity-100" : "pointer-events-none opacity-0"),
                onClick: onClose,
                "aria-hidden": "true"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn("fixed right-0 top-0 z-[1400] flex h-full w-[70%] max-w-[280px] flex-col bg-white transition-transform duration-300 ease-out", isOpen ? "translate-x-0" : "translate-x-full", className),
                role: "dialog",
                "aria-modal": "true",
                "aria-label": "필터",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex h-14 shrink-0 items-center justify-between border-b border-gray-50 px-5",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle1",
                                className: "text-primary",
                                children: "필터"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: onClose,
                                className: "flex size-5 items-center justify-center text-primary",
                                "aria-label": "닫기",
                                children: /*#__PURE__*/ _jsx(Icon.Close_2px, {
                                    className: "size-5"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-y-auto",
                        children: /*#__PURE__*/ _jsx(Accordion.Root, {
                            type: "multiple",
                            value: expandedItems,
                            onValueChange: setExpandedItems,
                            className: "flex flex-col",
                            children: filterItems.filter((filter)=>filter.items.length > 1).map((filter)=>/*#__PURE__*/ _jsxs(Accordion.Item, {
                                    value: filter.key,
                                    children: [
                                        /*#__PURE__*/ _jsx(FilterAccordionTrigger, {
                                            label: filter.name,
                                            isExpanded: expandedItems.includes(filter.key)
                                        }),
                                        /*#__PURE__*/ _jsx(FilterAccordionContent, {
                                            filterKey: filter.key,
                                            items: filter.items,
                                            selectedValues: selectedFilters[filter.key] || [],
                                            onItemChange: (value, checked)=>handleCheckboxChange(filter.key, value, checked)
                                        })
                                    ]
                                }, filter.key))
                        })
                    })
                ]
            })
        ]
    });
};
/**
 * 필터 아코디언 트리거
 */ const FilterAccordionTrigger = /*#__PURE__*/ forwardRef((param, ref)=>/*#__PURE__*/ {
    let { label , isExpanded  } = param;
    return _jsx(Accordion.Header, {
        children: /*#__PURE__*/ _jsxs(Accordion.Trigger, {
            ref: ref,
            className: "flex w-full items-center justify-between px-5 py-3",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: isExpanded ? "subtitle1" : "body1",
                    className: "text-primary",
                    children: label
                }),
                isExpanded ? /*#__PURE__*/ _jsx(Icon.ArrowUp_2px, {
                    className: "size-[18px] text-primary"
                }) : /*#__PURE__*/ _jsx(Icon.ArrowDown_2px, {
                    className: "size-[18px] text-primary"
                })
            ]
        })
    });
});
FilterAccordionTrigger.displayName = "FilterAccordionTrigger";
/**
 * 필터 아코디언 콘텐츠 - 메모이제이션으로 불필요한 리렌더링 방지
 */ const FilterAccordionContent = /*#__PURE__*/ memo((param)=>/*#__PURE__*/ {
    let { filterKey , items , selectedValues , onItemChange  } = param;
    return _jsx(Accordion.Content, {
        className: "overflow-hidden bg-gray-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        children: /*#__PURE__*/ _jsx("div", {
            className: "flex flex-col border-b border-gray-200 py-1",
            children: items.map((item)=>/*#__PURE__*/ _jsxs("label", {
                    className: "flex cursor-pointer items-center gap-3 px-5 py-2",
                    children: [
                        /*#__PURE__*/ _jsx(Checkbox, {
                            checked: selectedValues.includes(item),
                            onCheckedChange: (checked)=>onItemChange(item, checked === true)
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "text-primary",
                            children: getFilterItemLabel(filterKey, item)
                        })
                    ]
                }, item))
        })
    });
});
FilterAccordionContent.displayName = "FilterAccordionContent";
